import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';
import { DialogData } from 'src/app/models/dialog-data';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { SubService } from 'src/app/services/sub.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { BeUsuarioXRegra } from 'src/app/models/BeUsuarioXRegra';
import { BeUsuario } from 'src/app/models/BeUsuario';
import { BeRegra } from 'src/app/models/BeRegra';

@Component({
  selector: 'app-usuario-regras',
  templateUrl: './list-usuario-regras.component.html',
  styleUrls: ['./list-usuario-regras.component.css']
})
export class ListUsuarioRegrasComponent implements OnInit, OnDestroy {
  @Input() public usuario?: BeUsuario;

  public usuarioRegras: BeUsuarioXRegra[] = [];
  public colunasListaDados: ListaDadosColuna[] = [
    {
      campo: 'idRegra',
      campoExibicao: 'regra.noRegra',
      rotulo: 'Regra',
      tamanho: '100%',
      editavel: true,
      edicao: {
        tipoEditor: 'combobox',
        campoChave: 'idRegra',
        campoRotulo: 'noRegra',
        opcoes: [],
        filtroCombo: (opcao: BeRegra, row: BeUsuarioXRegra) => {
          return !this.usuarioRegras.some(item => item.idRegra === opcao.idRegra && item.idUsuarioXRegra !== row.idUsuarioXRegra);
        },
        validacao: {
          obrigatorio: true,
        },
      },
    },
  ];
  public subscription?: Subscription;
  public listaRegrasDisponiveis: BeRegra[] = [];

  public constructor(
    private httpClient: HttpClient,
    private sub: SubService,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
  ) {
  }

  public ngOnInit(): void {
    if (this.usuario?.listaUsuarioXRegras) {
      this.onResult(this.usuario.listaUsuarioXRegras);
    } else {
      this.onResult([]);
    }

    this.carregarTodasRegras();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onIniciarEdicaoLinha(usuarioXRegra: BeUsuarioXRegra): void {
    if (usuarioXRegra.idUsuarioXRegra) {
      return;
    }

    if (this.usuario?.idUsuario) {
      usuarioXRegra.idUsuario = this.usuario.idUsuario;
    }

    this.atualizarOpcoesRegra();
  }

  public onSalvarLinha(usuarioXRegra: BeUsuarioXRegra): void {
    const idUsuario = this.usuario?.idUsuario ?? usuarioXRegra.idUsuario;
    if (!idUsuario) {
      return;
    }

    const idRegra = Number(usuarioXRegra.idRegra);
    if (!Number.isFinite(idRegra)) {
      return;
    }

    const payload = {
      idUsuario: idUsuario,
      idRegra: idRegra,
    } as BeUsuarioXRegra;

    this.unsubscribe();
    this.subscription = this.httpClient.post<BeUsuarioXRegra>(`/api/UsuarioXRegras`, payload).subscribe({
      next: (res) => {
        this.unsubscribe();

        if (res?.idUsuarioXRegra) {
          usuarioXRegra.idUsuarioXRegra = res.idUsuarioXRegra;
        }

        usuarioXRegra.idUsuario = idUsuario;
        usuarioXRegra.idRegra = payload.idRegra;
        usuarioXRegra.regra = this.obterRegraPorId(payload.idRegra) ?? res?.regra ?? usuarioXRegra.regra;

        this.usuarioRegras = [...this.usuarioRegras];
        this.atualizarOpcoesRegra();
        this.snackBarService.exibirSucesso('Registro criado!');
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  public onDelete(usuarioXRegra: BeUsuarioXRegra) {
    this.showDialog('Deseja excluir esse registro?', 'Alerta', 'Não', 'Sim', usuarioXRegra);
  }

  public showDialog(msg: string, statusMsg: string, button: string = 'OK', secondButton: string = '', usuarioXRegra: BeUsuarioXRegra = {} as BeUsuarioXRegra) {
    if (this.subscription)
      return;
    const dialogData: DialogData = {
      title: statusMsg,
      message: msg,
      buttonText: button,
      secondButtonText: secondButton,
    };
    const dialogRef = this.dialog.open(DialogComponent, { data: dialogData });
    dialogRef.afterClosed().subscribe((result) => {
      this.unsubscribe();
      if (result) {
        this.sub.reset(this.httpClient.delete(`/api/UsuarioXRegras/${usuarioXRegra.idUsuarioXRegra}`),
          () => this.onDeleteConfirm(usuarioXRegra),
          () => this.showDialog('Não foi possível excluir o registro!', 'Erro')
        );
      }
    })
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private onResult(res: BeUsuarioXRegra[]) {
    this.usuarioRegras = (res ?? []).map(item => ({
      ...item,
      idUsuarioXRegra: Number(item.idUsuarioXRegra),
      idUsuario: Number(item.idUsuario),
      idRegra: Number(item.idRegra),
    }));

    this.atualizarOpcoesRegra();
  }

  private onDeleteConfirm(res: BeUsuarioXRegra) {
    const index = this.usuarioRegras.findIndex(item => item.idUsuarioXRegra === res.idUsuarioXRegra);
    if (index >= 0) {
      this.usuarioRegras.splice(index, 1);
      this.usuarioRegras = [...this.usuarioRegras];
    }

    this.atualizarOpcoesRegra();
    this.snackBarService.exibirSucesso('Registro excluído!');
  }

  private carregarTodasRegras(): void {
    this.unsubscribe();
    this.subscription = this.httpClient.get<BeRegra[]>(
      `/api/Regras`,
    ).subscribe({
      next: (res) => {
        this.unsubscribe();
        this.listaRegrasDisponiveis = (res ?? []).map(regra => ({
          ...regra,
          idRegra: Number(regra.idRegra),
        }));
        this.atualizarOpcoesRegra();
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  private atualizarOpcoesRegra(): void {
    this.colunasListaDados = this.colunasListaDados.map(coluna => {
      if (coluna.campo !== 'idRegra') {
        return coluna;
      }

      return {
        ...coluna,
        edicao: {
          ...coluna.edicao,
          tipoEditor: 'combobox',
          opcoes: [...this.listaRegrasDisponiveis].sort((a, b) => (a.noRegra || '').localeCompare(b.noRegra || '')),
        },
      };
    });
  }

  private obterRegraPorId(idRegra?: number): BeRegra | undefined {
    if (!idRegra) {
      return undefined;
    }

    const regraDisponivel = this.listaRegrasDisponiveis.find(regra => regra.idRegra === idRegra);
    if (regraDisponivel) {
      return regraDisponivel;
    }

    return this.usuarioRegras.find(item => item.idRegra === idRegra)?.regra;
  }

}
